import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { removeEntity, setEntities, setEntity, updateEntity, withEntities } from '@ngrx/signals/entities';
import { IrisDMSFolder, IrisDMSFolderI, IrisDmsRootFolderId } from '../models/IrisDMSFolder';
import { setError, setLoaded, setLoading, withRequestState } from '@iris/common/signals/features/request-status.feature';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { debounceTime, filter, mergeMap, pipe, switchMap } from 'rxjs';
import { IrisQueryParams, IrisQueryParamsBuilder } from '@iris/api-query';
import { computed, inject } from '@angular/core';
import { IrisFoldersService } from '@iris/common/services/docs/folders.service';
import { tapResponse } from '@ngrx/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { expandTree, getFolderPathByFolderId, mapFoldersToLine } from '../helpers/dms-folders.helper';
import { chain } from 'lodash';

export interface IrisDmsSystemFoldersState {
  offset: number;
  limit: number;
  total: number;
  isLoading: boolean;
}

export const defaultState: IrisDmsSystemFoldersState = {
  offset: 0,
  limit: 30,
  total: 0,
  isLoading: false,
};

export const IrisDmsSystemFoldersStore = signalStore(
  { providedIn: 'root', protectedState: false },
  withEntities<IrisDMSFolderI>(),
  withState<IrisDmsSystemFoldersState>(defaultState),
  withRequestState(),
  withComputed(store => ({
    hasNextPage: computed(() => store.entities().length >= store.offset()),
    buildVisibleFlatFoldersTree: computed(() => {
      const rootFolders = getFoldersByParentId(store.entities(), IrisDmsRootFolderId);
      return chain(rootFolders).map(folder => [folder, ...getFlatFoldersTree(store.entities(), folder)]).flatten().value();
    }),
  })),
  withMethods((
    store,
    foldersService = inject(IrisFoldersService),
  ) => ({
    fetchSystemFolders$: rxMethod<{
      offset: number;
      limit: number;
      queryParams?: IrisQueryParams;
    }>(
      pipe(
        debounceTime(100),
        filter(() => !store.loading()),
        switchMap(({ offset, limit, queryParams }) => {
          patchState(store, setLoading());
          const query = new IrisQueryParamsBuilder(queryParams).offset(offset).limit(limit).toStructure();
          return foldersService.getSystemFolders(query).pipe(
            tapResponse({
              next: ({ elements, count }) => {
                const systemFolders = mapFoldersToLine(elements, store.entityMap()).map(element => new IrisDMSFolder(element));

                patchState(
                  store,
                  setEntities(systemFolders),
                  { offset: offset + limit, total: count },
                  setLoaded(),
                );
              },
              error: (err: HttpErrorResponse) => patchState(store, setError(err.message)),
            }),
          );
        }),
      ),
    ),
    fetchParentFolder$: rxMethod<string>(
      pipe(
        mergeMap(folderId => {
          return foldersService.getParentFolders(folderId).pipe(
            tapResponse({
              next: folder => {
                const folders = mapFoldersToLine([folder], store.entityMap());
                patchState(store, setEntities(expandTree(folders, folderId)));
              },
              error: (err: HttpErrorResponse) => patchState(store, setError(err.message)),
            }),
          );
        }),
      ),
    ),
    fetchFolder$: rxMethod<string>(
      pipe(
        mergeMap(folderId => {
          return foldersService.getFolderById(folderId).pipe(
            tapResponse({
              next: folder => patchState(store, setEntities(mapFoldersToLine([folder], store.entityMap()))),
              error: (err: HttpErrorResponse) => patchState(store, setError(err.message)),
            }),
          );
        }),
      ),
    ),
    expandFolder$: rxMethod<string>(
      pipe(
        mergeMap(folderId => {
          return foldersService.getFolderById(folderId).pipe(
            tapResponse({
              next: folder => {
                const expandedFolder = new IrisDMSFolder({ ...folder, expanded: true });
                const folders = mapFoldersToLine([expandedFolder], store.entityMap());
                patchState(store, setEntities(folders));
              },
              error: (err: HttpErrorResponse) => patchState(store, setError(err.message)),
            }),
          );
        }),
      ),
    ),
    collapseFolder: (folderId: string) => {
      patchState(store, updateEntity({ id: folderId, changes: { expanded: false } }));
    },
    addFolder: (folder: IrisDMSFolderI) => patchState(store, setEntity(folder)),
    removeFolder: (folderId: string) => {
      const folderToRemove = store.entityMap()[folderId];
      if (!folderToRemove) { return; }
      let parentFolder = store.entityMap()[folderToRemove.parentId];
      if (parentFolder) {
        parentFolder = { ...parentFolder, children: [...parentFolder.children.filter(({ id }) => id !== folderId)] };
        patchState(store, updateEntity({ id: parentFolder.id, changes: parentFolder }));
      }
      patchState(store, removeEntity(folderId));
    },
    getFoldersByParentId: (parentFolderId: string) => computed(() => getFoldersByParentId(store.entities(), parentFolderId)),
  })),
  withMethods(store => ({
    loadNextPage: () => store.fetchSystemFolders$({ offset: store.offset(), limit: store.limit() }),
    getSubfolders: (parentFolderId: string) => computed(() => store.getFoldersByParentId(parentFolderId)()),
    getFolderPathByFolderId: (folderId: string) => computed(() => {
      const entities = store.entityMap();
      return getFolderPathByFolderId(folderId, entities);
    }),
    getFolderById: (folderId: string) => computed(() => store.entityMap()[folderId]),
  })),
);

function getFlatFoldersTree(folders: IrisDMSFolderI[], parentFolder: IrisDMSFolderI): IrisDMSFolderI[] {
  if (!parentFolder.hasChildren || !parentFolder.expanded) { return []; }
  const children = getFoldersByParentId(folders, parentFolder.id);
  return chain(children).map(child => [child, ...getFlatFoldersTree(folders, child)]).flatten().value();
};

function getFoldersByParentId(folders: IrisDMSFolderI[], parentId: string): IrisDMSFolderI[] {
  return folders.filter(folder => folder.parentId === parentId);
}
